import React, { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItemButton,
    ListItemText,
    IconButton,
    Button,
    Typography,
    SxProps,
    Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { SHIPPING_ACTION_LINK_SX } from "../../styles/shippingActionLinkStyles";
import { mapsUrlBuilder } from "@ecommerce/shared-utils";

export type BranchForPickup = {
    id?: string | null;
    nombre?: string | null;
    direccion?: string | null;
    codigoPostal?: string | null;
    latitud?: string | null;
    longitud?: string | null;
    [key: string]: any;
};

const getBranchStableId = (b: BranchForPickup, idx: number) => {
    // Preferimos el id real (ideal). Si no viene, generamos uno estable (no depende del index del filtrado).
    const fallback =
        [b.nombre, b.direccion, b.codigoPostal].filter(Boolean).join("|") || String(idx);
    return (b.id ?? fallback) as string;
};

type Props = {
    open: boolean;
    onClose: () => void;

    optionName?: string;
    providerName?: string;

    branches: BranchForPickup[];
    selectedBranchId?: string | null;

    onSelect: (branchId: string) => void;

    searchPlaceholder?: string;
    dialogSx?: SxProps<Theme>; // para zIndex en drawer
};

export function BranchPickerDialog({
    open,
    onClose,
    optionName,
    providerName,
    branches,
    selectedBranchId,
    onSelect,
    searchPlaceholder = "Buscar por nombre, dirección o CP",
    dialogSx,
}: Props) {
    const [search, setSearch] = useState("");

    const normalized = useMemo(
        () =>
            branches.map((b, idx) => ({
                ...b,
                __stableId: getBranchStableId(b, idx),
            })),
        [branches]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return normalized;

        return normalized.filter((b) => {
            const haystack = `${b.nombre ?? ""} ${b.direccion ?? ""} ${b.codigoPostal ?? ""}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [normalized, search]);

    React.useEffect(() => {
        if (open) setSearch("");
    }, [open, optionName]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={dialogSx}>
            <DialogTitle sx={{ fontFamily: "inherit", fontWeight: 600 }}>
                Seleccioná una sucursal
                {optionName ? ` - ${optionName}` : ""}
                <IconButton
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8, color: "#666" }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    size="small"
                    fullWidth
                    sx={{
                        mb: 2,
                        "& .MuiOutlinedInput-root": {
                            fontFamily: "inherit",
                            fontSize: "12px",
                        },
                    }}
                />

                <List dense sx={{ p: 0 }}>
                    {filtered.map((b, idx) => {
                        const branchId = b.__stableId as string;
                        const mapsUrl = mapsUrlBuilder.buildBranchUrl(providerName, b);

                        return (
                            <ListItemButton
                                key={branchId}
                                selected={branchId === selectedBranchId}
                                onClick={() => onSelect(branchId)}
                                sx={{
                                    border: "1px solid #f0f0f0",
                                    borderRadius: 1,
                                    mb: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <ListItemText
                                    primary={`${b.nombre ?? "Sucursal"}${b.codigoPostal ? ` (${b.codigoPostal})` : ""
                                        }`}
                                    secondary={b.direccion ?? ""}
                                    primaryTypographyProps={{
                                        sx: {
                                            fontFamily: "inherit",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            color: "#010101",
                                        },
                                    }}
                                    secondaryTypographyProps={{
                                        sx: { fontFamily: "inherit", fontSize: "12px", color: "#666" },
                                    }}
                                    sx={{ flex: 1, minWidth: 0, mr: 1 }}
                                />

                                {mapsUrl && (
                                    <Button
                                        component="a"
                                        href={mapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<RoomOutlinedIcon sx={{ fontSize: 16 }} />}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={SHIPPING_ACTION_LINK_SX}
                                    >
                                        Ver en mapa
                                    </Button>
                                )}
                            </ListItemButton>
                        );
                    })}

                    {!filtered.length && (
                        <Typography sx={{ fontFamily: "inherit", fontSize: "12px", color: "#666" }}>
                            No se encontraron sucursales con ese criterio.
                        </Typography>
                    )}
                </List>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" sx={BUTTON_STYLES.grey} onClick={onClose}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
}














